import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import type { ProductFilterMark } from '../../utils/productBrowserFilterState';
import { normalizeWikimediaThumbUrl, readableInkOn } from '../../utils/vendorLogos';

interface BrandMarkProps {
  mark: ProductFilterMark;
  /** Rendered box size in px — pills, menu rows and cards each use their own. */
  size: number;
  /** Initial's font size in px, sized to `size` by the call site. */
  fontSize: number;
  /**
   * Let wordmark logos keep their aspect ratio instead of shrinking inside a
   * square. Cards and the details header use this; compact pills stay square.
   */
  wide?: boolean;
}

/**
 * A brand's real logo when one resolves, and its colored initial when one
 * doesn't — including when the logo URL resolves but then fails to load.
 *
 * The call sites previously hid the broken <img> and rendered nothing in its
 * place, so a dead registry URL left an empty gap rather than falling back to
 * the initial. Many registry logos are dead today, which is exactly when the
 * fallback matters, so the failure has to flip to the initial instead of
 * removing the mark.
 */
export const BrandMark: React.FC<BrandMarkProps> = ({ mark, size, fontSize, wide = false }) => {
  const [failed, setFailed] = useState(false);

  // A different URL deserves a fresh attempt: without this the failed flag
  // latches when a re-render swaps the brand behind the same element.
  useEffect(() => {
    setFailed(false);
  }, [mark.logoUrl]);

  if (mark.logoUrl && !failed) {
    return (
      <Box
        component="img"
        src={normalizeWikimediaThumbUrl(mark.logoUrl)}
        alt=""
        sx={{
          flex: '0 0 auto',
          height: size,
          width: wide ? 'auto' : size,
          maxWidth: wide ? size * 4.5 : size,
          objectFit: 'contain',
          objectPosition: 'left center',
        }}
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <Box
      sx={{
        flex: '0 0 auto',
        width: size,
        height: size,
        borderRadius: '50%',
        bgcolor: mark.color,
        color: readableInkOn(mark.color),
        display: 'grid',
        placeItems: 'center',
        fontSize,
        fontWeight: 700,
      }}
    >
      {mark.initial}
    </Box>
  );
};
